import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import {
  StepStrip,
  WrittenBoard,
  createNumericInputTask,
  padDigits,
  randInt,
} from "../sharedWrittenNatural";
import {
  analyzeWrittenDivision,
  divisionRemainderCells,
} from "../writtenDivisionLayout";

export { analyzeWrittenDivision } from "../writtenDivisionLayout";

function noZeroDigits(value) {
  return !String(value).includes("0");
}

function pickExact({ divisorMin, divisorMax, quotientMin, quotientMax, dividendMin, dividendMax }) {
  for (let i = 0; i < 900; i += 1) {
    const divisor = randInt(divisorMin, divisorMax);
    const quotient = randInt(quotientMin, quotientMax);
    const dividend = divisor * quotient;
    if (
      dividend >= dividendMin &&
      dividend <= dividendMax &&
      quotient !== 11 &&
      noZeroDigits(quotient)
    ) {
      return { dividend, divisor, quotient, result: quotient, remainder: 0 };
    }
  }
  for (let divisor = divisorMin; divisor <= divisorMax; divisor += 1) {
    for (let quotient = quotientMin; quotient <= quotientMax; quotient += 1) {
      const dividend = divisor * quotient;
      if (dividend >= dividendMin && dividend <= dividendMax && quotient !== 11 && noZeroDigits(quotient)) {
        return { dividend, divisor, quotient, result: quotient, remainder: 0 };
      }
    }
  }
  return { dividend: 96, divisor: 8, quotient: 12, result: 12, remainder: 0 };
}

function pickWithRemainder({
  divisorMin,
  divisorMax,
  quotientMin,
  quotientMax,
  dividendMin,
  dividendMax,
}) {
  for (let i = 0; i < 900; i += 1) {
    const divisor = randInt(divisorMin, divisorMax);
    const quotient = randInt(quotientMin, quotientMax);
    const remainder = randInt(1, divisor - 1);
    const dividend = divisor * quotient + remainder;
    if (
      dividend >= dividendMin &&
      dividend <= dividendMax &&
      quotient !== 11 &&
      noZeroDigits(quotient)
    ) {
      return { dividend, divisor, quotient, result: quotient, remainder };
    }
  }
  for (let divisor = divisorMin; divisor <= divisorMax; divisor += 1) {
    for (let quotient = quotientMin; quotient <= quotientMax; quotient += 1) {
      for (let remainder = 1; remainder < divisor; remainder += 1) {
        const dividend = divisor * quotient + remainder;
        if (
          dividend >= dividendMin &&
          dividend <= dividendMax &&
          quotient !== 11 &&
          noZeroDigits(quotient)
        ) {
          return { dividend, divisor, quotient, result: quotient, remainder };
        }
      }
    }
  }
  return { dividend: 97, divisor: 8, quotient: 12, result: 12, remainder: 1 };
}

function pickTwoDigitByOneDigitExact() {
  return pickExact({
    divisorMin: 2,
    divisorMax: 9,
    quotientMin: 12,
    quotientMax: 49,
    dividendMin: 20,
    dividendMax: 99,
  });
}

function pickThreeDigitByOneDigitExact() {
  return pickExact({
    divisorMin: 2,
    divisorMax: 9,
    quotientMin: 21,
    quotientMax: 149,
    dividendMin: 100,
    dividendMax: 999,
  });
}

function pickThreeDigitByTwoDigitExact() {
  return pickExact({
    divisorMin: 11,
    divisorMax: 29,
    quotientMin: 12,
    quotientMax: 89,
    dividendMin: 100,
    dividendMax: 999,
  });
}

function pickFourDigitByTwoDigitExact() {
  return pickExact({
    divisorMin: 12,
    divisorMax: 49,
    quotientMin: 21,
    quotientMax: 199,
    dividendMin: 1000,
    dividendMax: 9999,
  });
}

function pickTwoDigitByOneDigitRemainder() {
  return pickWithRemainder({
    divisorMin: 2,
    divisorMax: 9,
    quotientMin: 12,
    quotientMax: 29,
    dividendMin: 20,
    dividendMax: 99,
  });
}

function pickThreeDigitByOneDigitRemainder() {
  return pickWithRemainder({
    divisorMin: 2,
    divisorMax: 9,
    quotientMin: 21,
    quotientMax: 149,
    dividendMin: 100,
    dividendMax: 999,
  });
}

function pickFourDigitByTwoDigitRemainder() {
  return pickWithRemainder({
    divisorMin: 12,
    divisorMax: 49,
    quotientMin: 21,
    quotientMax: 199,
    dividendMin: 1000,
    dividendMax: 9999,
  });
}

export function LongDivisionBoard({ step }) {
  const divisorDigits = String(step.divisor).split("");
  const rowValues = [
    step.dividend,
    step.divisor,
    step.quotientText,
    ...step.rows.flatMap((row) => [row.product, row.remainderText]),
  ];
  const cols = Math.max(...rowValues.map((value) => String(value).length), 2);
  const rightOffset = cols + 2;
  const activeQuotientColumn =
    step.activeKind !== "final" && step.activeQuotientIndex != null
      ? [step.activeQuotientIndex + Math.max(cols - String(step.dividend).length, 0)]
      : [];

  return (
    <WrittenBoard>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `1ch repeat(${cols}, 1.08ch) 1.2ch repeat(${Math.max(
            divisorDigits.length,
            1
          )}, 1.08ch)`,
          columnGap: "0.18em",
          alignItems: "end",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          lineHeight: 1.25,
        }}
      >
        <DigitCells
          digits={padDigits(step.quotientDigits?.join("") || step.quotientText, cols)}
          offset={2}
          row={1}
          highlightIndices={activeQuotientColumn}
        />
        <div
          style={{
            gridColumn: `2 / ${cols + 2}`,
            gridRow: 1,
            alignSelf: "end",
            borderBottom: "1px solid var(--color-text)",
            height: 0,
          }}
        />
        <DigitCells digits={padDigits(step.dividend, cols)} offset={2} row={2} />
        <span style={{ gridColumn: cols + 2, gridRow: 2 }}>:</span>
        <DigitCells digits={divisorDigits} offset={rightOffset + 1} row={2} />

        {step.rows.map((row, idx) => {
          const baseRow = 3 + idx * 2;
          const highlighted = step.activeRow === idx;
          const productCols = String(row.product).length;
          const productStart = row.start + String(row.current).length - productCols;
          const remainderCells = divisionRemainderCells(row);
          const firstDigitColumn = Math.max(productStart, 0) + 2;
          const signColumn = Math.max(firstDigitColumn - 1, 1);

          return (
            <React.Fragment key={idx}>
              <span style={{ gridColumn: signColumn, gridRow: baseRow, textAlign: "center" }}>-</span>
              <DigitCells
                digits={padDigits(row.product, productCols)}
                offset={firstDigitColumn}
                row={baseRow}
                highlightIndices={
                  highlighted ? Array.from({ length: productCols }, (_, colIdx) => colIdx) : []
                }
              />
              <div
                style={{
                  gridColumn: `${firstDigitColumn} / ${firstDigitColumn + productCols}`,
                  gridRow: baseRow,
                  alignSelf: "end",
                  borderBottom: "1px solid var(--color-text)",
                  height: 0,
                }}
              />
              <DigitCells
                digits={remainderCells.digits}
                offset={remainderCells.offset}
                row={baseRow + 1}
                highlightIndices={
                  highlighted
                    ? Array.from({ length: remainderCells.digits.length }, (_, colIdx) => colIdx)
                    : []
                }
              />
            </React.Fragment>
          );
        })}
      </div>
    </WrittenBoard>
  );
}

function DigitCells({ digits, offset = 1, row = 1, highlightIndices = [] }) {
  return (
    <>
      {digits.map((digit, idx) => {
        const visible = digit !== " " && digit !== "";
        const highlighted = highlightIndices.includes(idx);
        return (
          <span
            key={`${row}-${offset}-${idx}`}
            style={{
              gridColumn: offset + idx,
              gridRow: row,
              textAlign: "center",
              minWidth: "1.08ch",
              color: highlighted && visible ? "#f59e0b" : undefined,
              fontWeight: highlighted && visible ? 700 : undefined,
            }}
          >
            {visible ? digit : "\u00A0"}
          </span>
        );
      })}
    </>
  );
}

function DivisionDetails({ lines, divisor }) {
  return (
    <ul>
      {lines.map((line, idx) => (
        <li key={idx}>
          {line.skipped ? (
            <>
              <InlineMath math={`${line.current}<${divisor}`} />, więc bierzemy następną cyfrę.
            </>
          ) : (
            <>
              W <InlineMath math={`${line.current}`} /> mieści się{" "}
              <InlineMath math={`${line.quotientDigit}`} /> razy{" "}
              <InlineMath math={`${divisor}`} />, bo{" "}
              <InlineMath math={`${line.quotientDigit}\\cdot ${divisor}=${line.product}`} />.
              Odejmujemy <InlineMath math={`${line.current}-${line.product}=${line.remainder}`} />.
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function explain(data) {
  const written = analyzeWrittenDivision(data.dividend, data.divisor);
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> Dzielną zapisujemy pod poziomą kreską, dzielnik
        po prawej stronie po dwukropku, a iloraz u góry.
      </p>
      <p>
        <strong>Krok 2:</strong> Patrzymy, ile razy dzielnik mieści się w
        kolejnej części dzielnej. Iloraz wpisujemy u góry, potem mnożymy i
        odejmujemy pod odpowiednim rzędem wartości.
      </p>
      <DivisionDetails lines={written.detailLines} divisor={data.divisor} />
      <StepStrip
        alignItems="flex-start"
        steps={written.steps.map((step, idx) => (
          <LongDivisionBoard key={idx} step={step} />
        ))}
      />
      <p>
        <strong>Wynik:</strong>{" "}
        <InlineMath
          math={
            data.remainder
              ? `${data.quotient}\\text{ reszty }${data.remainder}`
              : `${data.quotient}`
          }
        />
      </p>
    </div>
  );
}

function makeExactTask(type, pickData) {
  return createNumericInputTask({
    type,
    pickData,
    renderTaskText: (data) => (
      <>
        Oblicz sposobem pisemnym: <InlineMath math={`${data.dividend}:${data.divisor}`} />.
      </>
    ),
    explain,
  });
}

function makeRemainderTask(type, pickData) {
  function generateTask() {
    return {
      type,
      data: pickData(),
      tags: ["liczby_naturalne", "dzielenie_pisemne", "dzielenie_z_reszta"],
    };
  }

  function renderTask({ data }) {
    return (
      <div style={{ textAlign: "left" }}>
        <p>
          Oblicz sposobem pisemnym: <InlineMath math={`${data.dividend}:${data.divisor}`} />.
          Podaj wynik i resztę.
        </p>
      </div>
    );
  }

  function renderInput(_task, userAnswer = {}, onChange, disabled) {
    const currentAnswer = userAnswer || {};
    const update = (key) => (value) => onChange({ ...currentAnswer, [key]: value });
    return (
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <FractionInput
          value={currentAnswer.quotient}
          onChange={update("quotient")}
          disabled={disabled}
          label="wynik"
          placeholder="np. 27"
        />
        <FractionInput
          value={currentAnswer.remainder}
          onChange={update("remainder")}
          disabled={disabled}
          label="reszta"
          placeholder="np. 3"
        />
      </div>
    );
  }

  function checkAnswer({ data }, userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object") return false;
    return userAnswer.quotient?.parsed === data.quotient && userAnswer.remainder?.parsed === data.remainder;
  }

  function renderAnswer({ data }) {
    return (
      <>
        <InlineMath math={`${data.quotient}`} /> reszty <InlineMath math={`${data.remainder}`} />
      </>
    );
  }

  function renderUserAnswer(userAnswer) {
    if (!userAnswer || typeof userAnswer !== "object") return <i>(brak odpowiedzi)</i>;
    const quotient = userAnswer.quotient?.raw;
    const remainder = userAnswer.remainder?.raw;
    if (!quotient && !remainder) return <i>(brak odpowiedzi)</i>;
    return (
      <>
        {quotient ? <InlineMath math={formatLatexPreview(quotient)} /> : <i>?</i>} reszty{" "}
        {remainder ? <InlineMath math={formatLatexPreview(remainder)} /> : <i>?</i>}
      </>
    );
  }

  return {
    generateTask,
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation: ({ data }) => explain(data),
  };
}

export const NaturalWrittenDivisionTwoByOneExactTask = makeExactTask(
  "NaturalWrittenDivisionTwoByOneExactTask",
  pickTwoDigitByOneDigitExact
);
export const NaturalWrittenDivisionThreeByOneExactTask = makeExactTask(
  "NaturalWrittenDivisionThreeByOneExactTask",
  pickThreeDigitByOneDigitExact
);
export const NaturalWrittenDivisionThreeByTwoExactTask = makeExactTask(
  "NaturalWrittenDivisionThreeByTwoExactTask",
  pickThreeDigitByTwoDigitExact
);
export const NaturalWrittenDivisionFourByTwoExactTask = makeExactTask(
  "NaturalWrittenDivisionFourByTwoExactTask",
  pickFourDigitByTwoDigitExact
);
export const NaturalWrittenDivisionTwoByOneRemainderTask = makeRemainderTask(
  "NaturalWrittenDivisionTwoByOneRemainderTask",
  pickTwoDigitByOneDigitRemainder
);
export const NaturalWrittenDivisionThreeByOneRemainderTask = makeRemainderTask(
  "NaturalWrittenDivisionThreeByOneRemainderTask",
  pickThreeDigitByOneDigitRemainder
);
export const NaturalWrittenDivisionFourByTwoRemainderTask = makeRemainderTask(
  "NaturalWrittenDivisionFourByTwoRemainderTask",
  pickFourDigitByTwoDigitRemainder
);

export const generateTask = NaturalWrittenDivisionTwoByOneExactTask.generateTask;
export const renderTask = NaturalWrittenDivisionTwoByOneExactTask.renderTask;
export const renderInput = NaturalWrittenDivisionTwoByOneExactTask.renderInput;
export const checkAnswer = NaturalWrittenDivisionTwoByOneExactTask.checkAnswer;
export const renderAnswer = NaturalWrittenDivisionTwoByOneExactTask.renderAnswer;
export const renderUserAnswer = NaturalWrittenDivisionTwoByOneExactTask.renderUserAnswer;
export const renderExplanation = NaturalWrittenDivisionTwoByOneExactTask.renderExplanation;
